import type { IABProduct, IABPurchaseId, IAPProduct, IAPPurchaseId } from "../lib/payment"
import { Scope } from "../lib/payment"
import { z } from "zod/v4"
import type { DocumentSnapshotLike } from "../firestore"
import { CoinPackId, type CoinPurchaseId, type ProductId, type UserId } from "../id"
import { CreatedAt, UpdatedAt } from "./system"
import { PlatformStoreSchema } from "./platform"

// Re-export Scope for convenience
export { Scope } from "../lib/payment"

export type CoinBill = {
  readonly to: UserId
  readonly total: number
  readonly scopes: Scope[]
}

export namespace CoinScopeUtil {
  const scopeLabelMap: Record<Scope, string> = {
    [Scope.Free]: "無料コイン",
    [Scope.IAP]: "App Store コイン",
    [Scope.IAB]: "Google Play コイン",
    [Scope.Web]: "Web コイン",
    [Scope.Compensation]: "補填コイン",
  }

  export function toLabel(scope: Scope): string {
    return scopeLabelMap[scope] ?? `不明 (${scope})`
  }

  export const scopeOptions = Object.entries(scopeLabelMap).map(([scope, label]) => ({
    value: String(scope),
    label,
  }))
}

export type CoinGrant = {
  readonly coins: number
  readonly bonus: number
}

export type IAPCoinPack = CoinGrant & { readonly product: IAPProduct }

export type IABCoinPack = CoinGrant & { readonly product: IABProduct }

export type CoinBalance = {
  readonly paidCoins: number
  readonly freeCoins: number
  readonly expiringFreeCoins: number
}

export type CoinExpiration = {
  readonly freeCoins: number
  readonly epoch: number
}

export type IAPCoinReclaim = {
  readonly token: string
  readonly product: IAPProduct
}

export type IABCoinReclaim = {
  readonly token: string
  readonly product: IABProduct
}

export type IAPCoinReclaimRecord = {
  readonly id: IAPPurchaseId
  readonly pack: IAPCoinPack
  readonly epoch: number
  readonly transaction: string
}

export type IABCoinReclaimRecord = {
  readonly id: IABPurchaseId
  readonly pack: IABCoinPack
  readonly epoch: number
}

export const CoinPackPopularity = z.enum(["normal", "popular"])
export type CoinPackPopularity = z.infer<typeof CoinPackPopularity>

export const CoinPackDocumentInsert = z.object({
  product_id: z.string().describe("このコインパックに対応した課金プラットフォーム上の商品の ID です。"),
  coins: z.number().describe("このコインパックの購入によって得られる有償コインの枚数です。"),
  bonus: z.number().describe("このコインパックの購入によって得られる無料コインの枚数です。"),
  price: z.number().describe("このコインパックの価格（unit単位）です。未成年の月間購入上限チェックに使用されます。"),
  unit: z.number().describe("価格の単位です。IAP (Apple) の場合は 1000 (milliunits)、IAB (Google) の場合は 1000000 (micros) です。"),
  purchase_limit: z.union([z.coerce.number().min(1).max(999), z.null()]).describe("1ユーザーあたりの購入回数上限です。未設定の場合は無制限です。").default(null),
  popularity: CoinPackPopularity.default("normal").describe("コインパックの人気フラグ: 通常 (normal) または 人気 (popular)"),
  popular_priority: z.int().min(1).max(99_999).default(1).describe("人気フラグ内での表示優先度（1-99999）"),
  priority: z.int().min(1).max(99_999).default(1).describe("全体での表示優先度（1-99999）"),
})

export const CoinPackDocumentUpdate = CoinPackDocumentInsert.pick({ popularity: true, purchase_limit: true, popular_priority: true, priority: true }).partial()

export type CoinPackDocumentUpdate = z.infer<typeof CoinPackDocumentUpdate>

export type CoinPackDocumentInsert = z.infer<typeof CoinPackDocumentInsert>

export const CoinPacksEdit = z.object({
  inserts: z.array(CoinPackDocumentInsert).optional(),
  reverts: z.array(
    z.string().transform(x => CoinPackId(x))
  ).optional(),
  removes: z.array(
    z.string().transform(x => CoinPackId(x))
  ).optional(),
})

export type CoinPacksEdit = z.infer<typeof CoinPacksEdit>

export const CoinPackStatus = z.union([
  z.literal("public"),
  z.literal("hidden")
])

export type CoinPackStatus = z.infer<typeof CoinPackStatus>

export const CoinPackDocumentBody = CoinPackDocumentInsert.extend({
  store: PlatformStoreSchema,
  app_domain: z.string().describe(`アプリを識別するコードです。
- Android
    - Gradle 上で指定する [applicationId](https://developer.android.com/build/configure-app-module#set-application-id)
- iOS
    - Info.plist の [Bundle Identifier](https://developer.apple.com/documentation/appstoreconnectapi/bundle_ids)
`),
  status: CoinPackStatus.describe("コインパックの公開状態を示します。"),
  created_at: CreatedAt,
  updated_at: UpdatedAt,
})

export type CoinPackDocumentBody = z.infer<typeof CoinPackDocumentBody>

export type CoinPackDocument = CoinPackDocumentBody & {
  id: CoinPackId,
}

export class CoinPackDocuments {

  static toDocumentData(doc: CoinPackDocumentBody): object {
    return {
      ...doc,
      // id は document path に入るので入れない
      id: undefined,
    }
  }

  static fromDocumentSnapshot(doc: DocumentSnapshotLike | null): CoinPackDocument | null {
    if (!doc) {
      return null
    }
    const data = doc.data()
    if (data === undefined) {
      return null
    }

    return {
      id: CoinPackId(doc.id),
      store: data["store"],
      app_domain: data["app_domain"],
      product_id: data["product_id"],
      coins: data["coins"],
      bonus: data["bonus"],
      price: data["price"],
      unit: data["unit"],
      popularity: data["popularity"] ?? "normal",
      popular_priority: data["popular_priority"] ?? 1,
      priority: data["priority"] ?? 1,
      status: data["status"],
      purchase_limit: data["purchase_limit"] ?? null,
      created_at: data["created_at"],
      updated_at: data["updated_at"],
    }
  }

  /**
   * Check if a coin pack purchase is allowed based on the current purchase count and purchase limit
   * @param pack The coin pack document
   * @param currentCount The current number of purchases made
   * @returns true if purchase is allowed, false if limit is reached
   */
  static canPurchase(pack: CoinPackDocument, currentCount: number): boolean {
    if (pack.purchase_limit === null) {
      return true
    }
    return currentCount < pack.purchase_limit
  }
}

export type CoinPurchase = {
  id: CoinPurchaseId
  product: {
    productId: ProductId
  }
  epoch: number
}

export const CoinPacksEditRequest = z
  .object({
    inserts: z.array(
      z.object({
        productId: CoinPackDocumentInsert.shape.product_id,
        coins: CoinPackDocumentInsert.shape.coins,
        bonus: CoinPackDocumentInsert.shape.bonus,
        price: CoinPackDocumentInsert.shape.price,
        unit: CoinPackDocumentInsert.shape.unit,
        purchaseLimit: CoinPackDocumentInsert.shape.purchase_limit,
        popularity: CoinPackDocumentInsert.shape.popularity,
        popularPriority: CoinPackDocumentInsert.shape.popular_priority,
        priority: CoinPackDocumentInsert.shape.priority,
      })
    ).optional(),
    reverts: z.array(z.string().transform(x => CoinPackId(x))).optional(),
    removes: z.array(z.string().transform(x => CoinPackId(x))).optional(),
  })

export type CoinPacksEditRequest = z.infer<typeof CoinPacksEditRequest>

export const CoinPacksEditResponse = CoinPacksEditRequest;

export type CoinPacksEditResponse = z.infer<typeof CoinPacksEditResponse>
